export interface Capabilities {
  /** Whether a WebGL context could be created at all. */
  webgl: boolean
  /** GL_MAX_TEXTURE_SIZE — often 2048 on low-end/locked-down Chromebooks. */
  maxTextureSize: number
  /** Max anisotropic filtering supported (1 if the extension is absent). */
  maxAnisotropy: number
  /** Weak device heuristic — used to cap spin FPS and pixel ratio. */
  lowPower: boolean
}

let cached: Capabilities | null = null

/**
 * Probe the device's WebGL capabilities once, using a throwaway context. Used to
 * decide between the 3D renderer and the 2D fallback, and to clamp texture size.
 */
export function detectCapabilities(): Capabilities {
  if (cached) return cached

  const canvas = document.createElement('canvas')
  const gl = (canvas.getContext('webgl2') ||
    canvas.getContext('webgl') ||
    canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null

  if (!gl) {
    cached = { webgl: false, maxTextureSize: 0, maxAnisotropy: 1, lowPower: true }
    return cached
  }

  const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number

  let maxAnisotropy = 1
  const ext =
    gl.getExtension('EXT_texture_filter_anisotropic') ||
    gl.getExtension('WEBKIT_EXT_texture_filter_anisotropic')
  if (ext) {
    maxAnisotropy = gl.getParameter(ext.MAX_TEXTURE_MAX_ANISOTROPY_EXT) as number
  }

  // Release the probe context promptly.
  gl.getExtension('WEBGL_lose_context')?.loseContext()

  const cores = navigator.hardwareConcurrency ?? 8
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 8
  const lowPower = (maxTextureSize || 2048) <= 2048 || cores <= 4 || memory <= 4

  cached = {
    webgl: true,
    maxTextureSize: maxTextureSize || 2048,
    maxAnisotropy: maxAnisotropy || 1,
    lowPower,
  }
  return cached
}
